/** The asset-type, type, and subtype filters over a list of assets. */
import { ChevronDown } from 'lucide-react';
import AutocompleteDropdown from './AutocompleteDropdown';
import { AppIcon } from './LucideIcon';
import {
  ASSET_KIND_LABELS,
  assetHasSubtypeTier,
  withAssetFilterTier,
  type AssetFilter,
  type AssetFilterOptions,
} from './assetFilterTiers';
import './asset-filters.css';

type Tier = {
  key: keyof AssetFilter;
  label: string;
  value: string;
  disabled: boolean;
  options: { value: string; label: string }[];
};

export default function AssetFilters({
  filter,
  options,
  onChange,
  layout = 'row',
  kindTier = 'select',
  elementTypeLabel = (elementType) => elementType,
}: {
  filter: AssetFilter;
  options: AssetFilterOptions;
  onChange: (next: AssetFilter) => void;
  /** `stack` is the rail's one-field-per-line form; `row` is the page's toolbar. */
  layout?: 'row' | 'stack';
  /** `fixed` omits the Asset type select: the caller sets `filter.kind`, as the asset rail's tab
   *  does, and a kind with no subtype tier (equipment, a line) shows no Subtype filter. */
  kindTier?: 'select' | 'fixed';
  /** The label a Type option shows for its value. */
  elementTypeLabel?: (elementType: string) => string;
}) {
  const allTiers: Tier[] = [
    {
      key: 'kind',
      label: 'Asset type',
      value: filter.kind,
      disabled: false,
      options: options.kinds.map((kind) => ({ value: kind, label: ASSET_KIND_LABELS[kind] })),
    },
    {
      key: 'elementType',
      label: 'Type',
      value: filter.elementType,
      disabled: !filter.kind,
      options: options.elementTypes.map((elementType) => ({ value: elementType, label: elementTypeLabel(elementType) })),
    },
    {
      key: 'subtype',
      label: 'Subtype',
      value: filter.subtype,
      disabled: !filter.elementType || !assetHasSubtypeTier(filter.kind),
      options: options.subtypes.map((subtype) => ({ value: subtype, label: subtype })),
    },
  ];
  const tiers = allTiers.filter((tier) => kindTier === 'select'
    || tier.key === 'elementType'
    || (tier.key === 'subtype' && assetHasSubtypeTier(filter.kind)));
  return (
    <div className={`asset-filters asset-filters--${layout}`}>
      {tiers.map((tier) => (tier.key === 'kind' ? (
        <label key={tier.key} className="asset-filter">
          <span className="asset-filter-label">{tier.label}</span>
          <span className="asset-filter-control">
            <select
              className="pnid-field pnid-select"
              value={tier.value}
              disabled={tier.disabled}
              onChange={(event) => onChange(withAssetFilterTier(filter, tier.key, event.target.value))}
            >
              <option value="">All</option>
              {tier.options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <AppIcon icon={ChevronDown} size={14} />
          </span>
        </label>
      ) : (
        <div key={tier.key} className="asset-filter">
          <span className="asset-filter-label" aria-hidden="true">{tier.label}</span>
          <AutocompleteDropdown
            ariaLabel={tier.label}
            value={tier.value}
            disabled={tier.disabled}
            options={tier.options}
            allowCustom={false}
            placeholder="All"
            emptyLabel="All"
            tabMovesFocus
            onChange={(value) => onChange(withAssetFilterTier(filter, tier.key, value))}
          />
        </div>
      )))}
    </div>
  );
}
